import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
  logout,
  getPendingTasks,
  getNotificationsTab,
  getNotificationById,
  getUnreadNotificationCount,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification
} from '../services/api';
import { notificationRoute } from '../utils/notificationRoute';
import useAutoRefresh from '../hooks/useAutoRefresh';
import Header from '../components/Header';
import RecordPaymentReceivedModal from '../components/RecordPaymentReceivedModal';
import CreateTransactionModal from '../components/CreateTransactionModal';
import PendingTasksModal from '../components/PendingTasksModal';
import NotificationsModal from '../components/NotificationsModal';
import AssignTransactionsModal from '../components/AssignTransactionsModal';
import RequestPaymentModal from '../components/RequestPaymentModal';
import SubmitPaymentReceivedModal from '../components/SubmitPaymentReceivedModal';
import './Dashboard.css';

function Dashboard({ user, setUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showPRModal, setShowPRModal] = useState(false);
  const [showCreateTransactionModal, setShowCreateTransactionModal] = useState(false);
  const [showPendingTasksModal, setShowPendingTasksModal] = useState(false);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [showAssignTransactionsModal, setShowAssignTransactionsModal] = useState(false);
  const [showRequestPaymentModal, setShowRequestPaymentModal] = useState(false);
  const [showPaymentReceivedModal, setShowPaymentReceivedModal] = useState(false);
  const [preSelectedTransaction, setPreSelectedTransaction] = useState(null);
  const [pendingTasks, setPendingTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [notifTab, setNotifTab] = useState('unread');
  const [notifCounts, setNotifCounts] = useState(null);

  // Handle navigation state for opening Assign Transactions modal
  useEffect(() => {
    if (location.state?.openAssignTransactions) {
      setShowAssignTransactionsModal(true);
      if (location.state?.preSelectedTransaction) {
        setPreSelectedTransaction(location.state.preSelectedTransaction);
      }
      // Clear the state to prevent reopening on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  const handleLogout = async () => {
    try {
      await logout();
      setUser(null);
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  useEffect(() => {
    if (user?.role === 'sales' || user?.role === 'administrator' || user?.role === 'finance') {
      fetchPendingTasks();
    }
    // Fetch notifications for all users
    if (user?.id) {
      fetchUnreadCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, user?.id]); // Only depend on role and id

  const fetchPendingTasks = async () => {
    setLoadingTasks(true);
    try {
      const tasks = await getPendingTasks();
      setPendingTasks(tasks || []);
    } catch (err) {
      console.error('Failed to fetch pending tasks:', err);
      console.error('Error details:', err.response?.data);
      setPendingTasks([]);
    } finally {
      setLoadingTasks(false);
    }
  };

  // Keep the pending-task pool fresh so the badge count and the modal never
  // show a task another finance user already claimed (denorm Phase 6 / EX3):
  // poll every 30s while visible + refetch on tab focus. Only the roles that
  // see the pool need it.
  const isTaskRole = user?.role === 'sales' || user?.role === 'administrator' || user?.role === 'finance';
  useAutoRefresh(fetchPendingTasks, { enabled: isTaskRole });


  const fetchUnreadCount = async () => {
    try {
      const result = await getUnreadNotificationCount();
      setUnreadCount(result.count || 0);
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
      setUnreadCount(0);
    }
  };

  // The unread badge previously fetched ONCE on mount — a user parked on this
  // page never saw new notifications until they navigated (the reported
  // "minutes of delay"). Same shared cadence: 30s while visible + on focus.
  useAutoRefresh(fetchUnreadCount, { enabled: !!user?.id });

  // Tabbed notifications (2026-08-13): unread / read / archived. The tab
  // slice, its counts and the paging all come from the server in one call;
  // rows migrate between tabs on their own because "archived" is DERIVED
  // (handled-by-others), so after any action we refetch rather than patch.
  const fetchNotifications = async (tab = notifTab, offset = 0, append = false) => {
    if (!append) setLoadingNotifications(true);
    try {
      const resp = await getNotificationsTab(tab, 30, offset);
      // Mid-deploy tolerance: an OLD server ignores ?tab and returns the bare
      // array — render it as the current tab with no counts rather than an
      // empty modal (the two tenants deploy minutes apart).
      const items = Array.isArray(resp) ? resp : (resp.items || []);
      const counts = Array.isArray(resp) ? null : (resp.counts || null);
      setNotifications(prev => (append ? [...prev, ...items] : items));
      setNotifCounts(counts);
      if (counts) setUnreadCount(counts.unread || 0);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
      if (!append) setNotifications([]);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const handleOpenNotifications = () => {
    setShowNotificationsModal(true);
    setNotifTab('unread');
    fetchNotifications('unread');
  };

  const handleNotifTabChange = (tab) => {
    setNotifTab(tab);
    setNotifications([]);
    fetchNotifications(tab);
  };

  const handleNotifLoadMore = () => {
    fetchNotifications(notifTab, notifications.length, true);
  };

  const handleMarkNotificationAsRead = async (notificationId) => {
    try {
      await markNotificationAsRead(notificationId);
      // Patch locally for instant feedback, then refetch the tab: reads
      // migrate rows between tabs server-side (an FYI archives on read), and
      // a later Load More would otherwise page against a shifted window and
      // skip a row (review 2026-08-13). In practice the click that marks a
      // row read also closes the modal, so the refetch mostly just keeps the
      // counts fresh for the next open — cheap either way.
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: 1 } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      fetchNotifications(notifTab);
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  // Web-push notifications land on /dashboard?notification=<id> (the service
  // worker can't know the in-app route). Resolve that id through the SAME
  // notificationRoute used by the bell-icon list so a tapped OS banner and an
  // in-list click always land in the same place. Runs at most once per mount
  // (pushLandingHandled ref) and always strips the query param afterward —
  // fail soft on any error, never block or error-toast the dashboard.
  const pushLandingHandled = useRef(false);
  useEffect(() => {
    if (pushLandingHandled.current) return;
    const params = new URLSearchParams(location.search);
    const rawId = params.get('notification');
    if (!rawId) return;
    pushLandingHandled.current = true;

    (async () => {
      try {
        const notificationId = parseInt(rawId, 10);
        // Fetch the ONE row directly — the old whole-list scan missed
        // anything older than a page once the list became paged.
        const row = await getNotificationById(notificationId).catch(() => null);
        if (row) {
          if (!row.is_read) {
            Promise.resolve(handleMarkNotificationAsRead(notificationId)).catch(() => {});
          }
          const destination = notificationRoute(row);
          if (destination) {
            navigate(destination.path, { state: destination.state, replace: true });
            return;
          }
        }
      } catch (err) {
        console.error('Failed to resolve push notification landing:', err);
      }
      // No destination (or lookup failed) — just drop the query param.
      navigate('/dashboard', { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead();
      // Every unread FYI just moved straight to Archived; open pool alerts
      // moved to Read. Refetch the tab — rows legitimately migrate.
      fetchNotifications(notifTab);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      await deleteNotification(notificationId);
      fetchNotifications(notifTab);
    } catch (err) {
      console.error('Failed to delete notification:', err);
    }
  };

  // Navigation sections by role. Each entry is { path, label, desc }.
  // Grouped into sections that read as natural workflow buckets rather than
  // a flat grid of emoji-laden feature cards.
  const navSections = (() => {
    if (user.role === 'administrator') {
      return [
        {
          title: 'Operations',
          items: [
            { path: '/task-tracking', label: 'Task Tracking', desc: 'Track all tasks and assignments' },
            { path: '/order-history', label: 'Order History', desc: 'View all orders and transactions' },
            { path: '/transaction-history', label: 'Transaction History', desc: 'View and manage all bank transactions' },
            { path: '/expenses', label: 'Expenses & Profit', desc: 'Expense, profit, deposit and withdrawal — submit claims or classify existing transactions (any currency)' }
          ]
        },
        {
          title: 'Accounts',
          items: [
            { path: '/money', label: 'Fiat Balance', desc: 'Track money across all bank accounts' },
            { path: '/crypto', label: 'Crypto Balance', desc: 'Manage crypto accounts and balances' },
            { path: '/customers', label: 'Customer & Address Management', desc: 'View all customers and reassign sales' }
          ]
        },
        {
          title: 'Platform & chain',
          items: [
            { path: '/platform', label: 'Platform Management', desc: 'Manage Binance, Bitget, Bybit, HTX, MEXC accounts' },
            { path: '/blockchain', label: 'Blockchain Monitor', desc: 'Monitor TRC20, ERC20, BEP20 transactions' }
          ]
        },
        {
          title: 'Admin & audit',
          items: [
            { path: '/users', label: 'User Management', desc: 'Create and manage user accounts' },
            { path: '/reports', label: 'Reports', desc: 'Traded volume, gross profit, expenses and net profit for any day or range' },
            { path: '/checkpoints', label: 'Checkpoints', desc: 'Immutable audit trail of every financial event' },
            { path: '/server-logs', label: 'Server Logs', desc: 'Monitor server activity and errors' },
            { path: '/ai-assistant', label: 'AI Assistant', desc: 'API key, Claude model per role, usage and spend' },
            { path: '/ai-staff', label: 'AI staff', desc: 'External AI accounts that record orders, their keys, limits and logs' }
          ]
        }
      ];
    }
    // Auditor (2026-10-24): a read-only account for an external auditor —
    // every business page an administrator has, minus the admin tooling
    // (User Management, Server Logs). The server refuses any change.
    if (user.role === 'auditor') {
      return [
        {
          title: 'Operations',
          items: [
            { path: '/task-tracking', label: 'Task Tracking', desc: 'View all tasks and assignments' },
            { path: '/order-history', label: 'Order History', desc: 'View all orders and transactions' },
            { path: '/transaction-history', label: 'Transaction History', desc: 'View all bank, crypto and blockchain transactions' },
            { path: '/expenses', label: 'Expenses & Profit', desc: 'View expense, profit, deposit and withdrawal entries' }
          ]
        },
        {
          title: 'Accounts',
          items: [
            { path: '/money', label: 'Fiat Balance', desc: 'View money across all bank accounts' },
            { path: '/crypto', label: 'Crypto Balance', desc: 'View crypto accounts and balances' },
            { path: '/customers', label: 'Customer & Address Management', desc: 'View all customers and their details' }
          ]
        },
        {
          title: 'Platform & chain',
          items: [
            { path: '/platform', label: 'Platform Management', desc: 'View platform orders and accounts' },
            { path: '/blockchain', label: 'Blockchain Monitor', desc: 'View TRC20, ERC20, BEP20 transactions' }
          ]
        },
        {
          title: 'Audit',
          items: [
            { path: '/reports', label: 'Reports', desc: 'Traded volume, gross profit, expenses and net profit for any day or range' },
            { path: '/checkpoints', label: 'Checkpoints', desc: 'Immutable audit trail of every financial event' }
          ]
        }
      ];
    }
    if (user.role === 'finance') {
      return [
        {
          title: 'Operations',
          items: [
            { path: '/task-tracking', label: 'Task Tracking', desc: 'Track and manage your assigned tasks' },
            { path: '/order-history', label: 'Order History', desc: 'View all orders and transactions' },
            { path: '/transaction-history', label: 'Transaction History', desc: 'View and manage all bank transactions' },
            { path: '/expenses', label: 'Expenses & Profit', desc: 'Expense, profit, deposit and withdrawal — submit claims or classify existing transactions (any currency)' }
          ]
        },
        {
          title: 'Accounts',
          items: [
            { path: '/money', label: 'Fiat Balance', desc: 'Track money across all bank accounts' },
            { path: '/crypto', label: 'Crypto Balance', desc: 'Manage crypto accounts and balances' },
            { path: '/customers', label: 'Customer & Address Management', desc: 'View all customers and their details' }
          ]
        },
        {
          title: 'Platform & chain',
          items: [
            { path: '/platform', label: 'Platform Management', desc: 'Manage platform orders and wallets' },
            { path: '/blockchain', label: 'Blockchain Monitor', desc: 'Monitor TRC20, ERC20, BEP20 transactions' }
          ]
        },
        {
          title: 'Audit',
          items: [
            { path: '/reports', label: 'Reports', desc: 'Traded volume, gross profit, expenses and net profit for any day or range' },
            { path: '/checkpoints', label: 'Checkpoints', desc: 'Immutable audit trail of every financial event' }
          ]
        }
      ];
    }
    // sales
    return [
      {
        title: 'Orders',
        items: [
          { path: '/create-order', label: 'Create Order', desc: 'Create buy/sell orders for customers' },
          { path: '/order-history', label: 'Order History', desc: 'View and manage your orders' },
          { path: '/task-tracking', label: 'Task Tracking', desc: 'Track your orders and payment progress' },
          { path: '/transaction-history', label: 'Transaction History', desc: 'View and manage all transactions' }
        ]
      },
      {
        title: 'Accounts',
        items: [
          { path: '/money', label: 'Fiat Balance', desc: 'View money across all bank accounts' },
          { path: '/crypto', label: 'Crypto Balance', desc: 'View crypto accounts and balances' },
          { path: '/customers', label: 'Customer & Address Management', desc: 'Manage your assigned customers' },
          { path: '/expenses', label: 'Expenses & Profit', desc: 'Expense, profit, deposit and withdrawal entries (multi-currency)' }
        ]
      },
      {
        title: 'Platform & chain',
        items: [
          { path: '/platform', label: 'Platform Management', desc: 'Submit platform orders with screenshots' },
          { path: '/blockchain', label: 'Blockchain Monitor', desc: 'Monitor TRC20, ERC20, BEP20 transactions' }
        ]
      },
      {
        title: 'Performance',
        items: [
          { path: '/my-performance', label: 'My performance', desc: 'Your own volume, margin and commission for any day or month' }
        ]
      }
    ];
  })();

  return (
    <div className="dashboard">
      <Header user={user} onLogout={handleLogout} />

      <div className="dashboard-content">
        <div className="dashboard-header">
          <h1>Dashboard</h1>
          <div className="dashboard-actions">
            <button
              type="button"
              onClick={() => { fetchPendingTasks(); setShowPendingTasksModal(true); }}
              className="btn btn-outline"
            >
              Pending tasks
              {pendingTasks.length > 0 && (
                <span className="btn-count">{pendingTasks.length}</span>
              )}
            </button>

            <button
              type="button"
              onClick={handleOpenNotifications}
              className="btn btn-outline"
            >
              Notifications
              {unreadCount > 0 && (
                <span className="btn-count">{unreadCount}</span>
              )}
            </button>

            {(user?.role === 'sales' || user?.role === 'administrator') && (
              <button
                type="button"
                onClick={() => setShowAssignTransactionsModal(true)}
                className="btn btn-outline"
              >
                Assign transactions
              </button>
            )}

            {user?.role === 'sales' && (
              <button
                type="button"
                onClick={() => setShowRequestPaymentModal(true)}
                className="btn btn-primary"
                title="Request finance to pay out a customer's balance to their bank account"
              >
                Request Payment
              </button>
            )}

            {user?.role === 'sales' && (
              <button
                type="button"
                onClick={() => setShowPaymentReceivedModal(true)}
                className="btn btn-outline"
                title="Upload a receipt a customer gave you so finance can record the payment received"
              >
                Payment Received
              </button>
            )}

            {user?.role === 'finance' && (
              <button
                type="button"
                onClick={() => setShowCreateTransactionModal(true)}
                className="btn btn-outline"
                title="Direct customer-to-customer ledger transfer (飞账)"
              >
                Create Transaction
              </button>
            )}

            {user?.role === 'finance' && (
              <button
                type="button"
                onClick={() => setShowPRModal(true)}
                className="btn btn-primary"
              >
                Create PR
              </button>
            )}
          </div>
        </div>

        <div className="nav-sections">
          {navSections.map(section => (
            <div key={section.title} className="nav-section">
              <div className="nav-section-title">{section.title}</div>
              <ul className="nav-list">
                {section.items.map(item => (
                  <li key={item.path}>
                    <Link to={item.path} className="nav-item">
                      <span className="nav-item-label">{item.label}</span>
                      <span className="nav-item-desc">{item.desc}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

              {/* Create PR Modal — the SAME component finance uses to record a
                  sales-submitted receipt, in its task-less mode. There is one
                  payment-received writer now; `task={null}` is the only
                  difference between the two entry points. */}
              {user?.role === 'finance' && (
                <RecordPaymentReceivedModal
                  isOpen={showPRModal}
                  task={null}
                  onClose={() => setShowPRModal(false)}
                  onSuccess={fetchPendingTasks}
                />
              )}

              {/* Create Transaction Modal (customer-to-customer 飞账) */}
              {user?.role === 'finance' && (
                <CreateTransactionModal
                  isOpen={showCreateTransactionModal}
                  onClose={() => setShowCreateTransactionModal(false)}
                  onSuccess={() => {}}
                />
              )}

              {/* Pending Tasks Modal */}
              {(user?.role === 'sales' || user?.role === 'administrator' || user?.role === 'finance') && (
                <PendingTasksModal
                  isOpen={showPendingTasksModal}
                  onClose={() => setShowPendingTasksModal(false)}
                  pendingTasks={pendingTasks}
                  onTaskUpdate={fetchPendingTasks}
                  user={user}
                />
              )}

              {/* Notifications Modal */}
              {showNotificationsModal && (
                <NotificationsModal
                  notifications={notifications}
                  counts={notifCounts}
                  tab={notifTab}
                  onTabChange={handleNotifTabChange}
                  onLoadMore={handleNotifLoadMore}
                  onClose={() => setShowNotificationsModal(false)}
                  onMarkAsRead={handleMarkNotificationAsRead}
                  onMarkAllAsRead={handleMarkAllAsRead}
                  onDelete={handleDeleteNotification}
                  loading={loadingNotifications}
                />
              )}

              {/* Assign Transactions Modal.
                  MOUNTED FOR FINANCE TOO (2026-08-02 bug fix). Task Tracking
                  offers finance an "Assign to customer" button on every open
                  pr_notification, and it navigates HERE — but this mount was
                  gated to sales||administrator, so a finance user landed on the
                  dashboard and nothing happened. The gate now matches SERVER
                  TRUTH: POST /api/transactions|crypto-transactions|blockchain
                  -transactions/:id/assign-to-customer is requireAuth +
                  requireCustomerActionAccess, and requireCustomerActionAccess
                  only constrains SALES (a sales user may act solely on their own
                  customers); finance and admin pass through. So finance is
                  allowed to assign and must be able to reach the modal.
                  The nav BUTTON above stays sales/admin — finance reaches this
                  from its task, which is the workflow it actually has. */}
              {(user?.role === 'sales' || user?.role === 'administrator' || user?.role === 'finance') && (
                <AssignTransactionsModal
                  isOpen={showAssignTransactionsModal}
                  onClose={() => {
                    setShowAssignTransactionsModal(false);
                    setPreSelectedTransaction(null);
                  }}
                  user={user}
                  preSelectedTransaction={preSelectedTransaction}
                />
              )}

              {/* Request Payment Modal (sales asks finance to pay out a customer's balance) */}
              {user?.role === 'sales' && (
                <RequestPaymentModal
                  user={user}
                  isOpen={showRequestPaymentModal}
                  onClose={() => setShowRequestPaymentModal(false)}
                  onSuccess={fetchPendingTasks}
                />
              )}

              {/* Submit Payment Received Modal (sales uploads a receipt a customer gave them, for finance to record) */}
              {user?.role === 'sales' && (
                <SubmitPaymentReceivedModal
                  user={user}
                  isOpen={showPaymentReceivedModal}
                  onClose={() => setShowPaymentReceivedModal(false)}
                  onSuccess={fetchPendingTasks}
                />
              )}
            </div>
          );
        }

        export default Dashboard;
